import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface SystemMessage {
  id: string;
  title: string;
  body: string;
  created_at: string;
  target_type: 'all' | 'all_clients' | 'all_practitioners' | 'specific_user';
}

const LAST_VIEWED_KEY = 'system_messages_last_viewed';

export const useUnreadSystemMessages = (userType: 'client' | 'practitioner') => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) {
        setUnreadCount(0);
        return;
      }

      try {
        const nowIso = new Date().toISOString();

        // Get the last time user viewed system messages
        const lastViewedStr = localStorage.getItem(`${LAST_VIEWED_KEY}_${user.id}`);
        const lastViewed = lastViewedStr ? new Date(lastViewedStr) : new Date(0);

        // Fetch messages created after last viewed time
        const { data, error } = await supabase
          .from('system_messages')
          .select('id, title, body, created_at, target_type')
          .eq('is_active', true)
          .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
          .gte('created_at', lastViewed.toISOString())
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching unread system messages:', error);
          setUnreadCount(0);
          return;
        }

        // Filter based on RLS logic (same as SystemMessagesList)
        const filteredMessages = (data || []).filter((msg: SystemMessage) => {
          const isRelevant = (
            msg.target_type === 'all' ||
            (msg.target_type === 'all_clients' && userType === 'client') ||
            (msg.target_type === 'all_practitioners' && userType === 'practitioner') ||
            (msg.target_type === 'specific_user')
          );
          return isRelevant;
        });

        setUnreadCount(filteredMessages.length);
      } catch (err) {
        console.error('Unexpected error fetching unread messages:', err);
        setUnreadCount(0);
      }
    };

    fetchUnreadCount();

    // Set up real-time subscription for new messages
    const channel = supabase
      .channel('unread-system-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_messages'
        },
        () => {
          // When a new message is inserted, refetch the count
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, userType]);

  const markAsViewed = useCallback(() => {
    if (!user) return;

    // Update the last viewed timestamp
    localStorage.setItem(`${LAST_VIEWED_KEY}_${user.id}`, new Date().toISOString());
    setUnreadCount(0);
  }, [user]);

  return { unreadCount, markAsViewed };
};
